import React, { useState } from 'react';
import {
    Container, Row, Col, Card, Form, InputGroup, Button, Dropdown, DropdownButton, Accordion
} from 'react-bootstrap';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer } from 'recharts';

// Utility function
const calculateMonthlyPI = (loanAmount, annualRate, years) => {
    const monthlyRate = annualRate / 100 / 12;
    const n = years * 12;
    if (monthlyRate === 0) return loanAmount / n;
    return (
        (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, n)) /
        (Math.pow(1 + monthlyRate, n) - 1)
    );
};

const COLORS = ['#002f6c', '#2ca7b0', '#b4d455', '#c7b3ff'];

export default function MortgageCalculator() {
    const [homePrice, setHomePrice] = useState(525000);
    const [downPayment, setDownPayment] = useState(105000);
    const [interestRate, setInterestRate] = useState(6.75);
    const [loanYears, setLoanYears] = useState(30);
    const [taxRate, setTaxRate] = useState(2.15);
    const [insuranceCost, setInsuranceCost] = useState(1575);
    const [hoaFees, setHoaFees] = useState(0);
    const [showMI, setShowMI] = useState(false);
    const [includeUtilities, setIncludeUtilities] = useState(false);
    const [utilities, setUtilities] = useState({
        water: 0,
        garbage: 0,
        electricity: 0,
        gas: 0
    });

    const downPaymentPct = homePrice ? (downPayment / homePrice) * 100 : 0;
    const loanAmount = Math.max(homePrice - downPayment, 0);

    const principalInterest = calculateMonthlyPI(loanAmount, interestRate, loanYears);
    const propertyTaxes = (homePrice * (taxRate / 100)) / 12;
    const monthlyInsurance = insuranceCost / 12;
    const mortgageInsurance = showMI ? (homePrice * 0.005) / 12 : 0;
    const utilityTotal = includeUtilities ? Object.values(utilities).reduce((a, b) => a + Number(b), 0) : 0;

    const totalMonthly = principalInterest + propertyTaxes + monthlyInsurance + mortgageInsurance + hoaFees + utilityTotal;

    const chartData = [
        { name: 'Principal & Interest', value: principalInterest },
        { name: 'Property Taxes', value: propertyTaxes },
        { name: 'Home Insurance', value: monthlyInsurance },
        { name: 'Mortgage Ins. & Other', value: mortgageInsurance + hoaFees + utilityTotal }
    ];

    return (
        <Container className="my-4">
            <Card className="shadow-sm p-4">
                <Row>
                    <Col md={4}>
                        <h5 className="fw-bold mb-1">Monthly Payment</h5>
                        <Accordion defaultActiveKey="0" alwaysOpen className="bg-light rounded p-3">

                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Home Price</Accordion.Header>
                                <Accordion.Body>
                                    <InputGroup className="mb-2">
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={homePrice}
                                            onChange={(e) => setHomePrice(Number(e.target.value))}
                                        />
                                    </InputGroup>
                                    <Form.Range
                                        min={100000}
                                        max={2000000}
                                        step={10000}
                                        value={homePrice}
                                        onChange={(e) => setHomePrice(Number(e.target.value))}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="1">
                                <Accordion.Header>Down Payment</Accordion.Header>
                                <Accordion.Body>
                                    <InputGroup className="mb-2">
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={downPayment}
                                            onChange={(e) => setDownPayment(Number(e.target.value))}
                                        />
                                    </InputGroup>
                                    <Form.Range
                                        min={0}
                                        max={homePrice}
                                        step={1000}
                                        value={downPayment}
                                        onChange={(e) => setDownPayment(Number(e.target.value))}
                                    />
                                    <InputGroup className="mt-2">
                                        <InputGroup.Text>%</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={downPaymentPct.toFixed(0)}
                                            onChange={(e) =>
                                                setDownPayment((Number(e.target.value) / 100) * homePrice)
                                            }
                                        />
                                    </InputGroup>
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="2">
                                <Accordion.Header>Loan Program</Accordion.Header>
                                <Accordion.Body>
                                    <DropdownButton
                                        size="sm"
                                        id="loan-term"
                                        title={`${loanYears}-year fixed`}
                                        onSelect={(k) => setLoanYears(Number(k))}
                                    >
                                        {[30, 20, 15, 10].map((yr) => (
                                            <Dropdown.Item key={yr} eventKey={yr}>
                                                {yr}-year fixed
                                            </Dropdown.Item>
                                        ))}
                                    </DropdownButton>
                                    <Form.Label className="mt-2">Interest Rate</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>%</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={interestRate}
                                            onChange={(e) => setInterestRate(Number(e.target.value))}
                                        />
                                    </InputGroup>
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="3">
                                <Accordion.Header>Property Taxes</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Label>Tax Rate (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={taxRate}
                                        onChange={(e) => setTaxRate(Number(e.target.value))}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="4">
                                <Accordion.Header>Home Insurance</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Label>Annual Insurance Cost</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={insuranceCost}
                                            onChange={(e) => setInsuranceCost(Number(e.target.value))}
                                        />
                                    </InputGroup>
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="5">
                                <Accordion.Header>Mortgage Insurance</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Check
                                        type="switch"
                                        label="Include mortgage insurance"
                                        checked={showMI}
                                        onChange={(e) => setShowMI(e.target.checked)}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="6">
                                <Accordion.Header>HOA Fees</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Label>Monthly HOA Fees</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={hoaFees}
                                            onChange={(e) => setHoaFees(Number(e.target.value))}
                                        />
                                    </InputGroup>
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="7">
                                <Accordion.Header>Utilities</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Check
                                        type="switch"
                                        label="Include utilities in payment"
                                        checked={includeUtilities}
                                        onChange={(e) => setIncludeUtilities(e.target.checked)}
                                    />
                                    {includeUtilities && (
                                        <>
                                            {['water', 'garbage', 'electricity', 'gas'].map((key) => (
                                                <InputGroup key={key} className="mt-2">
                                                    <InputGroup.Text>{key.charAt(0).toUpperCase() + key.slice(1)}</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={utilities[key]}
                                                        onChange={(e) =>
                                                            setUtilities((prev) => ({
                                                                ...prev,
                                                                [key]: Number(e.target.value)
                                                            }))
                                                        }
                                                    />
                                                </InputGroup>
                                            ))}
                                        </>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                        </Accordion>
                    </Col>

                    <Col md={8}>
                        <Row className="gy-4">
                            {/* Chart and Monthly Total */}
                            <Col md={12} className="text-center d-flex flex-column justify-content-center">
                                <div style={{ width: '100%', aspectRatio: "2" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius="65%"
                                                outerRadius="95%"
                                                startAngle={90}
                                                endAngle={-270}
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '0.8rem' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="fs-3 fw-bold mt-2">
                                    ${totalMonthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </div>
                                <div className="text-muted">/month</div>
                            </Col>

                            {/* Monthly Breakdown */}
                            <Col md={6}>
                                <ul className="list-unstyled fs-6">
                                    <li className="d-flex justify-content-between border-bottom py-1">
                                        <span>Principal &amp; Interest</span>
                                        <span>${principalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                    </li>
                                    <li className="d-flex justify-content-between border-bottom py-1">
                                        <span>Property Taxes</span>
                                        <span>${propertyTaxes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                    </li>
                                    <li className="d-flex justify-content-between border-bottom py-1">
                                        <span>Home Insurance</span>
                                        <span>${monthlyInsurance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                    </li>
                                    {mortgageInsurance > 0 && (
                                        <li className="d-flex justify-content-between border-bottom py-1">
                                            <span>Mortgage Ins. &amp; Other</span>
                                            <span>${(mortgageInsurance + hoaFees + utilityTotal).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                        </li>
                                    )}
                                </ul>
                            </Col>

                            {/* Action Buttons */}
                            <Col md={6} className="d-flex flex-column justify-content-center">
                                <Button variant="outline-dark" className="w-100 mb-2">
                                    Get Pre‑Qualified
                                </Button>
                                <Button variant="secondary" className="w-100">
                                    See today’s mortgage rates
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Card>
        </Container>
    );
}